import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { apiService } from "../services/api";

const ASSESSMENT_TYPES = [
  { key: "cra", label: "CRA Applicability" },
  { key: "cra-compliance", label: "CRA Compliance" },
  { key: "sdl", label: "SDL Maturity" },
  { key: "data-act", label: "Data Act" },
];

interface ImportResultItem {
  productId: string;
  productName: string;
  success: boolean;
  error?: string;
  action: string;
}

interface ImportResponse {
  assessmentType: string;
  totalRows: number;
  totalSucceeded: number;
  totalFailed: number;
  results: ImportResultItem[];
}

export default function AssessmentImportPage() {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState(ASSESSMENT_TYPES[0]!.key);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await apiService.importAssessment(selectedType, file);
      if (res.data?.success) {
        setResult(res.data.data as ImportResponse);
      } else {
        setError(res.data?.error || "Import failed");
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "An unexpected error occurred";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const token = localStorage.getItem("token");
    const url = apiService.getImportTemplateUrl(selectedType);
    // Open in new tab with auth
    const a = document.createElement("a");
    a.href = url + (url.includes("?") ? "&" : "?") + "token=" + token;
    a.target = "_blank";
    a.click();
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>
      <h1 className="text-2xl font-bold mb-6">Import Assessment from Excel</h1>

      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Step 1: Select type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            1. Select Assessment Type
          </label>
          <select
            value={selectedType}
            onChange={(e) => {
              setSelectedType(e.target.value);
              setFile(null);
              setResult(null);
              setError(null);
            }}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {ASSESSMENT_TYPES.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Step 2: Download template */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            2. Download the Standard Template
          </label>
          <button
            onClick={handleDownloadTemplate}
            className="inline-flex items-center gap-2 rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Download Template (
            {ASSESSMENT_TYPES.find((t) => t.key === selectedType)?.label})
          </button>
          <p className="mt-1 text-xs text-gray-500">
            Fill in the Product ID and answers, then upload below.
          </p>
        </div>

        {/* Step 3: Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            3. Upload Filled Excel File
          </label>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setResult(null);
              setError(null);
            }}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        {/* Import button */}
        <button
          onClick={handleImport}
          disabled={!file || loading}
          className="w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {loading ? "Importing…" : "Import Assessment Data"}
        </button>

        {/* Error */}
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="rounded-md bg-green-50 px-4 py-3 flex-1 text-center">
                <p className="text-2xl font-bold text-green-700">
                  {result.totalSucceeded}
                </p>
                <p className="text-xs text-green-600">Succeeded</p>
              </div>
              <div className="rounded-md bg-red-50 px-4 py-3 flex-1 text-center">
                <p className="text-2xl font-bold text-red-700">
                  {result.totalFailed}
                </p>
                <p className="text-xs text-red-600">Failed</p>
              </div>
              <div className="rounded-md bg-gray-50 px-4 py-3 flex-1 text-center">
                <p className="text-2xl font-bold text-gray-700">
                  {result.totalRows}
                </p>
                <p className="text-xs text-gray-600">Total Rows</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-500">
                      Product ID
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500">
                      Product Name
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500">
                      Action / Error
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {result.results.map((r, i) => (
                    <tr key={i} className={r.success ? "" : "bg-red-50"}>
                      <td className="px-4 py-2 font-mono text-xs">
                        {r.productId}
                      </td>
                      <td className="px-4 py-2">{r.productName || "—"}</td>
                      <td className="px-4 py-2">
                        {r.success ? (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                            ✓ Success
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                            ✗ Failed
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {r.success ? r.action : r.error}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
